/**
 * PersonalInformation Component
 * 
 * Handles the first step of the registration process, collecting basic personal information
 * including name, emails, phone numbers, and personal webpage.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data state
 * @param {Function} props.handleChange - Function to handle form field changes
 * @returns {JSX.Element} Personal information form step
 */

import React from 'react';
import { salutations, countryCodes } from '../../constants/types';

const PersonalInformation = ({ formData, handleChange }) => {
  const formatName = (value) => value
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  const formatEmail = (value) => value.toLowerCase();

  const handleNameChange = (e) => {
    const { name, value } = e.target;
    handleChange({ target: { name, value: formatName(value) } });
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    handleChange({ target: { name, value: formatEmail(value) } });
  };

  return (
    <div>
      <h4 className="mb-4">Personal Information</h4>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="salutation" className="form-label">
            Salutation <span className="text-danger">*</span>
          </label>
          <select
            className="form-select form-control"
            id="salutation"
            name="salutation"
            value={formData.salutation}
            onChange={handleChange}
            required
          >
            <option value="">Select...</option>
            {salutations.map((sal) => (
              <option key={sal} value={sal}>
                {sal}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="firstName" className="form-label">
            First Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleNameChange}
            required
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="middleName" className="form-label">
            Middle Name
          </label>
          <input
            type="text"
            className="form-control"
            id="middleName"
            name="middleName"
            value={formData.middleName}
            onChange={handleNameChange}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="lastName" className="form-label">
            Last Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleNameChange}
            required
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="primaryEmail" className="form-label">
            Primary Email Address <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            className="form-control"
            id="primaryEmail"
            name="primaryEmail"
            value={formData.primaryEmail}
            onChange={handleEmailChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="primaryEmailConfirm" className="form-label">
            Primary Email Confirm <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            className="form-control"
            id="primaryEmailConfirm"
            name="primaryEmailConfirm"
            value={formData.primaryEmailConfirm}
            onChange={handleEmailChange}
            required
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryEmail" className="form-label">
            Secondary Email Address
          </label>
          <input
            type="email"
            className="form-control"
            id="secondaryEmail"
            name="secondaryEmail"
            value={formData.secondaryEmail}
            onChange={handleEmailChange}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryEmailConfirm" className="form-label">
            Secondary Email Confirm
          </label>
          <input
            type="email"
            className="form-control"
            id="secondaryEmailConfirm"
            name="secondaryEmailConfirm"
            value={formData.secondaryEmailConfirm}
            onChange={handleEmailChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">Work Phone</label>
          <div className="input-group">
            <select
              className="form-select"
              style={{ maxWidth: "100px" }}
              name="workPhoneCountry"
              value={formData.workPhoneCountry}
              onChange={handleChange}
            >
              {countryCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              className="form-control"
              name="workPhone"
              placeholder="Phone number"
              value={formData.workPhone}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label">Cell Phone</label>
          <div className="input-group">
            <select
              className="form-select"
              style={{ maxWidth: "100px" }}
              name="cellPhoneCountry"
              value={formData.cellPhoneCountry}
              onChange={handleChange}
            >
              {countryCodes.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              type="tel"
              className="form-control"
              name="cellPhone"
              placeholder="Phone number"
              value={formData.cellPhone}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="personalWebPage" className="form-label">
          Personal Web Page (http://)
        </label>
        <input
          type="url"
          className="form-control"
          id="personalWebPage"
          name="personalWebPage"
          placeholder="http://"
          value={formData.personalWebPage}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};

export default PersonalInformation;
